// Role sidebar — persistent 20% left panel with role list and create button.

import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Role } from '@/lib/api/usersRoles'
import { cn } from '@/lib/utils'

interface RoleSidebarProps {
  roles: Role[]
  selectedRoleId: string | null
  onSelectRole: (role: Role) => void
  onCreateClick: () => void
  isLoading?: boolean
}

export function RoleSidebar({
  roles,
  selectedRoleId,
  onSelectRole,
  onCreateClick,
  isLoading,
}: RoleSidebarProps) {
  return (
    <div className="flex h-full flex-col border-r">
      {/* Create button */}
      <div className="border-b p-3">
        <Button size="sm" className="w-full" onClick={onCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          New Role
        </Button>
      </div>

      {/* Role list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : roles.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No roles created yet.
          </div>
        ) : (
          <div className="py-1">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => onSelectRole(role)}
                className={cn(
                  'w-full px-3 py-2.5 text-left transition-colors hover:bg-muted/50',
                  selectedRoleId === role.id && 'bg-muted',
                )}
              >
                <div className="truncate text-sm font-medium">{role.name}</div>
                <div className="text-xs text-muted-foreground">
                  {role.member_count} member{role.member_count !== 1 ? 's' : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
